import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import {
  previewPanelAndText,
  savePanelAndText,
  type PanelAndTextPreviewResponse,
  type PanelAndTextRequest,
  type PanelAndTextResponse,
} from './api';

/**
 * Sebastian ruling 2026-09-17 fix-5 (items C + D) — combined panel-
 * and-text hooks used by the new approval-screen editor.
 *
 * ═══ usePanelAndTextPreview ═════════════════════════════════════════
 *
 * Debounced live-preview loop. The combined editor calls
 * `schedule(request)` on every change (keystroke, drag release,
 * width edit); the hook waits `DEBOUNCE_MS` (~400 ms) after the LAST
 * schedule call before firing the read-only preview endpoint.
 * Rapid succession collapses to a single trailing call.
 *
 * `endpointAvailable` starts true; on the first 404 the hook flips
 * it to false and stops firing further previews. Sebastian ruling
 * 2026-09-17 fix-5 item F — deploy-order safety: an old backend
 * without the preview endpoint must not crash the editor. Once
 * disabled, the caller falls back to no-live-preview (client-side
 * geometry updates without a server round-trip).
 *
 * ═══ useSavePanelAndText ═══════════════════════════════════════════
 *
 * Fires the combined PATCH. Same 404 detection: on 404 the hook
 * flips `combinedEndpointAvailable` to false and the caller falls
 * back to the pre-fix-5 separate endpoints (move + resize + text)
 * per item F.
 */

const DEBOUNCE_MS = 400;

export type PreviewResult =
  | { readonly ok: true; readonly response: PanelAndTextPreviewResponse }
  | { readonly ok: false; readonly error: ApiError | null; readonly endpoint404: boolean };

export interface UsePanelAndTextPreviewState {
  readonly previewing: boolean;
  readonly error: ApiError | null;
  readonly endpointAvailable: boolean;
  readonly schedule: (request: PanelAndTextRequest) => void;
  readonly cancel: () => void;
}

export function usePanelAndTextPreview(
  orderId: string,
  sceneId: string,
  onResult: (result: PreviewResult) => void,
): UsePanelAndTextPreviewState {
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [endpointAvailable, setEndpointAvailable] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(0);

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => cancel, [cancel]);

  const schedule = useCallback(
    (request: PanelAndTextRequest) => {
      if (!endpointAvailable) return;
      cancel();
      timerRef.current = setTimeout(async () => {
        timerRef.current = null;
        const seq = ++inFlightRef.current;
        setPreviewing(true);
        setError(null);
        try {
          const response = await previewPanelAndText(orderId, sceneId, request);
          if (seq !== inFlightRef.current) return; // superseded
          onResult({ ok: true, response });
        } catch (err) {
          if (seq !== inFlightRef.current) return;
          const apiError = err instanceof ApiError ? err : null;
          const endpoint404 =
            apiError !== null && apiError.httpStatus === 404 && apiError.envelope === null;
          if (endpoint404) {
            setEndpointAvailable(false);
          }
          setError(apiError);
          onResult({ ok: false, error: apiError, endpoint404 });
        } finally {
          if (seq === inFlightRef.current) setPreviewing(false);
        }
      }, DEBOUNCE_MS);
    },
    [orderId, sceneId, onResult, cancel, endpointAvailable],
  );

  return { previewing, error, endpointAvailable, schedule, cancel };
}

export type SaveResult =
  | { readonly ok: true; readonly response: PanelAndTextResponse }
  | { readonly ok: false; readonly error: ApiError | null; readonly endpoint404: boolean };

export interface UseSavePanelAndTextState {
  readonly saving: boolean;
  readonly error: ApiError | null;
  readonly combinedEndpointAvailable: boolean;
  readonly save: (request: PanelAndTextRequest) => Promise<SaveResult>;
}

export function useSavePanelAndText(
  orderId: string,
  sceneId: string,
): UseSavePanelAndTextState {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [combinedEndpointAvailable, setCombinedEndpointAvailable] = useState(true);

  const save = useCallback(
    async (request: PanelAndTextRequest): Promise<SaveResult> => {
      setSaving(true);
      setError(null);
      try {
        const response = await savePanelAndText(orderId, sceneId, request);
        return { ok: true, response };
      } catch (err) {
        const apiError = err instanceof ApiError ? err : null;
        const endpoint404 =
          apiError !== null && apiError.httpStatus === 404 && apiError.envelope === null;
        if (endpoint404) {
          setCombinedEndpointAvailable(false);
        }
        setError(apiError);
        return { ok: false, error: apiError, endpoint404 };
      } finally {
        setSaving(false);
      }
    },
    [orderId, sceneId],
  );

  return { saving, error, combinedEndpointAvailable, save };
}
